'use client';

import { useState } from 'react';
import ToolHero from '@/components/tools/ToolHero';
import ToolCTA from '@/components/tools/ToolCTA';
import TagCloud from '@/components/tools/TagCloud';
import CopyButton from '@/components/tools/CopyButton';
import {
  keywordDictionary,
  type CategoryKey,
} from '@/lib/tool-data/keyword-dictionary';

interface MatchedKeyword {
  text: string;
  count: number;
}

interface CategoryResult {
  key: CategoryKey;
  label: string;
  variant: 'blue' | 'green' | 'purple' | 'orange' | 'gray';
  matches: MatchedKeyword[];
}

function extractKeywords(text: string): CategoryResult[] {
  const normalizedText = text.toLowerCase();
  const results: CategoryResult[] = [];

  for (const [key, category] of Object.entries(keywordDictionary)) {
    const matches: MatchedKeyword[] = [];

    for (const entry of category.keywords) {
      const termsToCheck = [entry.keyword, ...(entry.aliases || [])];
      let totalCount = 0;

      for (const term of termsToCheck) {
        // Build a regex that matches the term as a whole word/phrase
        const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`\\b${escaped}\\b`, 'gi');
        const found = normalizedText.match(regex);
        if (found) {
          totalCount += found.length;
        }
      }

      if (totalCount > 0) {
        // Avoid duplicate entries (in case a keyword appeared via alias)
        if (!matches.some((m) => m.text === entry.keyword)) {
          matches.push({ text: entry.keyword, count: totalCount });
        }
      }
    }

    // Sort by count descending
    matches.sort((a, b) => b.count - a.count);

    if (matches.length > 0) {
      results.push({
        key: key as CategoryKey,
        label: category.label,
        variant: category.variant,
        matches,
      });
    }
  }

  // Sort categories by number of matches descending
  results.sort((a, b) => b.matches.length - a.matches.length);

  return results;
}

const categoryIcons: Record<CategoryKey, string> = {
  hard_skills: '🎯',
  soft_skills: '🤝',
  certifications: '📜',
  tools_technologies: '🛠️',
  industry_terms: '📊',
};

export default function KeywordExtractorContent() {
  const [jobDescription, setJobDescription] = useState('');
  const [results, setResults] = useState<CategoryResult[] | null>(null);

  const totalKeywords = results
    ? results.reduce((sum, cat) => sum + cat.matches.length, 0)
    : 0;
  const totalCategories = results ? results.length : 0;

  const allKeywordsText = results
    ? results
        .flatMap((cat) => cat.matches.map((m) => m.text))
        .join(', ')
    : '';

  const handleExtract = () => {
    if (!jobDescription.trim()) return;
    const extracted = extractKeywords(jobDescription);
    setResults(extracted);
  };

  const handleClear = () => {
    setJobDescription('');
    setResults(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-stone-100">
      <ToolHero
        title="Resume Keyword Extractor"
        description="Paste any job description and instantly extract all the important keywords, skills, and requirements. Organized by category and importance level."
        category="Resume Tool"
      />

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Input Section */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mb-8">
          <label
            htmlFor="job-description"
            className="block text-lg font-bold text-gray-900 mb-2"
          >
            Paste a Job Description
          </label>
          <p className="text-sm text-gray-600 mb-4">
            Copy and paste the full job description below. We will scan it against
            300+ keywords across 5 categories.
          </p>
          <textarea
            id="job-description"
            rows={10}
            value={jobDescription}
            onChange={(e) => setJobDescription(e.target.value)}
            placeholder="Paste the full job description here..."
            className="w-full border border-gray-300 rounded-xl px-4 py-3 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y text-sm leading-relaxed"
          />

          <div className="flex flex-wrap items-center gap-3 mt-4">
            <button
              onClick={handleExtract}
              disabled={!jobDescription.trim()}
              className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-bold py-3 px-8 rounded-xl shadow text-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Extract Keywords
            </button>
            {jobDescription && (
              <button
                onClick={handleClear}
                className="text-sm text-gray-500 hover:text-gray-700 transition"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Results Section */}
        {results !== null && (
          <div className="space-y-6">
            {/* Summary Bar */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 flex flex-wrap items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {totalKeywords === 0
                    ? 'No keywords found'
                    : `Found ${totalKeywords} keyword${totalKeywords !== 1 ? 's' : ''} across ${totalCategories} categor${totalCategories !== 1 ? 'ies' : 'y'}`}
                </h2>
                {totalKeywords > 0 && (
                  <p className="text-sm text-gray-600 mt-1">
                    Keywords are sorted by frequency within each category.
                  </p>
                )}
              </div>
              {totalKeywords > 0 && (
                <CopyButton text={allKeywordsText} label="Copy All Keywords" />
              )}
            </div>

            {totalKeywords === 0 && (
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 text-center">
                <p className="text-gray-600">
                  No recognized keywords were found. Try pasting a longer or more
                  detailed job description.
                </p>
              </div>
            )}

            {/* Category Cards */}
            {totalKeywords > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {results.map((cat) => (
                  <div
                    key={cat.key}
                    className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <span>{categoryIcons[cat.key]}</span>
                        {cat.label}
                      </h3>
                      <span className="text-sm font-semibold text-gray-500 bg-gray-100 px-2.5 py-0.5 rounded-full">
                        {cat.matches.length}
                      </span>
                    </div>
                    <TagCloud
                      tags={cat.matches.map((m) => ({
                        text: m.text,
                        count: m.count,
                      }))}
                      variant={cat.variant}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Educational Section */}
        <section className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mt-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Why Resume Keywords Matter</h3>
          <div className="space-y-4 text-gray-700 leading-relaxed">
            <p>
              Resume keywords are the specific skills, qualifications, tools, and industry terms that employers include in their job descriptions. When you apply for a job, your resume is almost always screened by an Applicant Tracking System (ATS) before a human ever reads it. These systems compare the keywords in your resume against the job posting to determine whether you are a strong enough match to move forward.
            </p>
            <p>
              Research shows that over 75% of resumes are rejected by ATS software before reaching a recruiter. The primary reason is a lack of keyword alignment between the resume and the job description. Extracting keywords from a job posting and thoughtfully incorporating them into your resume is one of the most effective strategies for getting past automated screening and landing interviews.
            </p>
            <h4 className="text-lg font-bold text-gray-900 pt-2">How to Use Extracted Keywords Effectively</h4>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <strong>Use exact phrasing from the job description</strong> -- if the posting says &quot;project management,&quot; use that exact phrase rather than a synonym like &quot;managing projects.&quot;
              </li>
              <li>
                <strong>Include keywords in context</strong> -- weave them naturally into your bullet points and summary rather than stuffing them into a hidden list.
              </li>
              <li>
                <strong>Prioritize hard skills and tools</strong> -- technical skills, software names, and certifications carry the most weight in ATS matching algorithms.
              </li>
              <li>
                <strong>Add a dedicated Skills section</strong> -- this gives the ATS a concentrated block of keywords to match against and ensures nothing is missed.
              </li>
              <li>
                <strong>Only include keywords that truthfully apply</strong> -- misrepresenting your qualifications will backfire during interviews and on the job.
              </li>
            </ul>
            <p className="mt-6 pt-4 border-t border-gray-200 text-sm text-gray-500">
              <strong>Sources &amp; Further Reading:</strong>{' '}
              <a href="https://www.indeed.com/career-advice/resumes-cover-letters/resume-keywords" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Indeed: How to Use Resume Keywords</a>
              {' · '}
              <a href="https://www.careeronestop.org/JobSearch/Resumes/ResumeGuide/TopResumeTips/tips-for-writing-your-resume.aspx" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">CareerOneStop Resume Tips (U.S. DOL)</a>
              {' · '}
              <a href="https://www.dol.gov/general/topic/employment" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">U.S. Department of Labor: Employment Resources</a>
            </p>
          </div>
        </section>

        {/* CTA */}
        <ToolCTA
          headline="Now match these keywords to your resume"
          description="Superpower Resume automatically tailors your resume to every job description, weaving in the right keywords so you pass ATS filters and impress recruiters."
          buttonText="Create a Keyword-Optimized Resume"
        />
      </div>
    </div>
  );
}
